import React, { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { ExternalLink, Filter, ArrowRight } from "lucide-react";
import "../styles/Portfolio.css";

// ===== IMPORT LOCAL ASSETS =====
import blindBarberImg from "../assets/case-studies/blindbarber.jpg";
import tillermanImg from "../assets/case-studies/tillerman.jpg";
import townsendImg from "../assets/case-studies/townsend.jpg";
import shantytownImg from "../assets/case-studies/shantytown.jpg";
import scissorsImg from "../assets/case-studies/scissorsscotch.jpg";
import twispriverSuitesImg from "../assets/case-studies/twispriver.jpg";
import honeyrunImg from "../assets/case-studies/honeyrun.jpg";
import graceCottageImg from "../assets/case-studies/gracecottage.jpg";
import twispriverInnImg from "../assets/case-studies/twispriver.jpg";
import modocImg from "../assets/case-studies/modoc.jpg";

const Portfolio = () => {
  const [activeFilter, setActiveFilter] = useState("All");

  const filters = ["All", "Web App", "Mobile App"];

  const projects = [
    {
      id: "blindbarber",
      title: "Blind Barber",
      tagline: "Barbershop + eCommerce site",
      category: "Web App",
      image: blindBarberImg,
      website: "https://blindbarber.com/",
      tags: ["React", "Shopify/eCommerce", "Branding"],
      results: "Brand & sales uplift",
    },
    {
      id: "thetillermanvt",
      title: "The Tillerman VT",
      tagline: "Restaurant website & booking platform",
      category: "Web App",
      image: tillermanImg,
      website: "https://www.thetillermanvt.com/",
      tags: ["React", "Reservations", "Hospitality"],
      results: "Increased online bookings",
    },
    {
      id: "townsendbarandgrill",
      title: "Townsend Bar & Grill",
      tagline: "Local bar & grill website revamp",
      category: "Web App",
      image: townsendImg,
      website: "https://townsendbarandgrill.com/",
      tags: ["React", "Tailwind", "Dining"],
      results: "Venue visibility +50%",
    },
    {
      id: "shantytownbarandgrill",
      title: "Shantytown Bar & Grill",
      tagline: "Bar & restaurant online presence",
      category: "Web App",
      image: shantytownImg,
      website: "https://shantytownbarandgrill.com/",
      tags: ["React", "UX/UI", "Hospitality"],
      results: "Website traffic +60%",
    },
    {
      id: "scissorsscotch",
      title: "Scissors & Scotch",
      tagline: "Barbershop + lounge website",
      category: "Web App",
      image: scissorsImg,
      website: "https://scissorsscotch.com/home",
      tags: ["React", "Booking", "Lifestyle"],
      results: "Online appointments +35%",
    },
    {
      id: "twispriversuites",
      title: "Twi-Spriver Suites",
      tagline: "Boutique lodging website",
      category: "Web App",
      image: twispriverSuitesImg,
      website: "https://twispriversuites.com/",
      tags: ["React", "Hospitality", "Reservations"],
      results: "Direct bookings doubled",
    },
    {
      id: "innathoneyrun",
      title: "Inn at Honey Run",
      tagline: "Resort & lodging site redesign",
      category: "Web App",
      image: honeyrunImg,
      website: "https://www.innathoneyrun.com",
      tags: ["React", "Tailwind", "Resort"],
      results: "Engagement +70%",
    },
    {
      id: "gracecottage",
      title: "Grace Cottage",
      tagline: "Countryside retreat website",
      category: "Web App",
      image: graceCottageImg,
      website: "https://gracecottage.org/",
      tags: ["React", "Hospitality", "Minimalism"],
      results: "Bookings improved",
    },
    
    {
      id: "modocmedicalcenter",
      title: "Modoc Medical Center",
      tagline: "Medical centre web platform",
      category: "Web App",
      image: modocImg,
      website: "https://modocmedicalcenter.org/",
      tags: ["React", "HealthTech", "Accessibility"],
      results: "Patient calls +30%",
    },
  ];

  const filteredProjects =
    activeFilter === "All"
      ? projects
      : projects.filter((p) => p.category === activeFilter);

  return (
    <div className="portfolio-page">
      <Navbar />

      <section className="portfolio-hero">
        <div className="container">
          <div className="hero-content fade-in-up">
            <h1 className="page-title">Our Work</h1>
            <p className="page-subtitle">
              Corporate platforms + websites & apps for small-town USA businesses
            </p>
          </div>
        </div>
      </section>

      <section className="portfolio-content">
        <div className="container">
          <div className="filter-bar">
            <div className="filter-label">
              <Filter size={20} />
              <span>Filter by:</span>
            </div>

            <div className="filter-buttons">
              {filters.map((filter) => (
                <button
                  key={filter}
                  className={`filter-btn ${
                    activeFilter === filter ? "active" : ""
                  }`}
                  onClick={() => setActiveFilter(filter)}
                >
                  {filter}
                </button>
              ))}
            </div>
          </div>

          <div className="portfolio-grid">
            {filteredProjects.map((project) => (
              <div className="portfolio-card" key={project.id}>
                <div className="portfolio-image">
                  <img src={project.image} alt={project.title} />
                  <div className="portfolio-overlay">
                    <span className="view-details">
                      View Case Study <ArrowRight size={16} />
                    </span>
                  </div>
                </div>

                <div className="portfolio-info">
                  <div className="portfolio-category">{project.category}</div>
                  <h3>{project.title}</h3>
                  <p className="portfolio-tagline">{project.tagline}</p>

                  {project.tags && (
                    <div className="portfolio-tags">
                      {project.tags.map((tag, i) => (
                        <span className="tag" key={i}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {project.results && (
                    <div className="portfolio-results">
                      <strong>Results:</strong> {project.results}
                    </div>
                  )}

                  <div className="portfolio-actions">
                    {project.website && (
                      <a
                        href={project.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-secondary"
                      >
                        Visit Website <ExternalLink size={16} />
                      </a>
                    )}

                    <Link to={`/work/${project.id}`} className="btn btn-primary">
                      Case Study
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredProjects.length === 0 && (
            <div className="no-results">
              <p>No projects found in this category.</p>
            </div>
          )}
        </div>
      </section>

    </div>
  );
};

export default Portfolio;
